import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Twitter, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useAuth } from '../contexts/AuthContext';
import InlineEditor from '../components/ui/InlineEditor';
import InlineTextArea from '../components/ui/InlineTextArea';

const ContactPage = () => {
  const { getSetting } = useSiteSettings();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const MIN_MESSAGE_LEN = 10;

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const validate = useMemo(() => {
    const errors = {};
    if (!form.name) errors.name = 'Champ requis';
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Email invalide';
    if (!form.message || form.message.length < MIN_MESSAGE_LEN) errors.message = `Au moins ${MIN_MESSAGE_LEN} caractères`;
    return errors;
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(validate).length > 0) {
      // Mark required fields as touched to surface errors
      setTouched(prev => ({ ...prev, name: true, email: true, message: true }));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Erreur');
      setStatus({ type: 'success', message: 'Message envoyé. Nous vous répondrons rapidement.' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (e) {
      setStatus({ type: 'error', message: "Impossible d'envoyer le message pour le moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const mapSrc = getSetting('contact_map_embed_url', '');

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Hero */}
      <section className="relative py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/15 to-purple-600/15 pointer-events-none"></div>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-5xl lg:text-7xl font-bold text-white mb-6">
            <InlineEditor settingKey="contact_page_title_fr" defaultValue="Contactez-nous">
              {getSetting('contact_page_title_fr', 'Contactez-nous')}
            </InlineEditor>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto">
            <InlineTextArea settingKey="contact_page_subtitle_fr" defaultValue={'Nous serions ravis d\'échanger avec vous sur vos projets et vos rêves de bijoux'}>
              {getSetting('contact_page_subtitle_fr', "Nous serions ravis d'échanger avec vous sur vos projets et vos rêves de bijoux")}
            </InlineTextArea>
          </motion.p>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-12 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6 lg:p-10">
            <h3 className="text-2xl font-bold text-white mb-6">Envoyez-nous un message</h3>

            {status.type === 'success' && (
              <div className="mb-6 flex items-center gap-3 rounded-lg px-4 py-3 bg-green-500/10 border border-green-500/40 text-green-300">
                <CheckCircle className="h-5 w-5" />
                <span>{status.message}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nom complet</label>
                <input name="name" value={form.name} onChange={handleChange} onBlur={handleBlur} className={`w-full px-4 py-3 rounded-lg bg-gray-700 border ${touched.name && validate.name ? 'border-red-500' : 'border-gray-600'} focus:outline-none focus:ring-2 ${touched.name && validate.name ? 'focus:ring-red-500' : 'focus:ring-yellow-500'}`} placeholder="Votre nom"/>
                {touched.name && validate.name && (
                  <p className="mt-1 text-xs text-red-400">Veuillez entrer votre nom complet.</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Email</label>
                <input name="email" value={form.email} onChange={handleChange} onBlur={handleBlur} className={`w-full px-4 py-3 rounded-lg bg-gray-700 border ${touched.email && validate.email ? 'border-red-500' : 'border-gray-600'} focus:outline-none focus:ring-2 ${touched.email && validate.email ? 'focus:ring-red-500' : 'focus:ring-yellow-500'}`} placeholder="votre@email.com"/>
                {touched.email && validate.email && (
                  <p className="mt-1 text-xs text-red-400">Veuillez saisir une adresse email valide.</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Téléphone (facultatif)</label>
                <input name="phone" value={form.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="+33..."/>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Sujet</label>
                <input name="subject" value={form.subject} onChange={handleChange} className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="Sujet du message"/>
              </div>
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm text-gray-300">Message</label>
                  <span className={`text-xs ${form.message.length < MIN_MESSAGE_LEN ? 'text-red-400' : 'text-gray-400'}`}>
                    {form.message.length}/{MIN_MESSAGE_LEN} min
                  </span>
                </div>
                <textarea name="message" value={form.message} onChange={handleChange} onBlur={handleBlur} rows={6} className={`w-full px-4 py-3 rounded-lg bg-gray-700 border ${touched.message && validate.message ? 'border-red-500' : 'border-gray-600'} focus:outline-none focus:ring-2 ${touched.message && validate.message ? 'focus:ring-red-500' : 'focus:ring-yellow-500'}`} placeholder="Une question sur nos creations ? Écrivez-nous !"></textarea>
                {touched.message && validate.message && (
                  <p className="mt-1 text-xs text-red-400">Votre message doit contenir au moins {MIN_MESSAGE_LEN} caractères.</p>
                )}
              </div>
              <div className="lg:col-span-2 flex justify-end">
                <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-semibold px-6 py-3 rounded-lg transition-colors disabled:opacity-60">
                  <Send className={`h-4 w-4 ${submitting ? 'animate-pulse' : ''}`} />
                  {submitting ? 'Envoi...' : 'Envoyer le message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;